// Manage Users: the people who can use the desk. A table with name, email, system role and how many students each holds,
// an Add User dialog (with password and confirmation), and edit, deactivate and delete. Real accounts, held by the
// standalone server; passwords are hashed there and never come back to the browser.
import { useState } from 'react';

import { Avatar, ConfirmDialog, DataTable, Drawer, FilterBar, IconButton, Notice, type Column } from 'src/front-components/lead-desk/kit';
import { Field } from 'src/front-components/lead-desk/student-form';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { PageMasthead, ShellCard, ShellChip } from 'src/front-components/theme/page-chrome';
import { DeskButton, DeskInput, DeskSelect, ErrorLine, LiveStamp } from 'src/front-components/theme/desk-controls';
import { getPalette } from 'src/front-components/theme/ewings-theme';
import { getShellColors, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';
import { useLiveData } from 'src/front-components/utils/desk-data';
import { createUser, deleteUser, fetchUsers, updateUser } from 'src/front-components/utils/lead-desk-store';
import {
  activeCounsellors,
  assignedCount,
  EMPTY_USER_FORM,
  initials,
  USER_ROLE_LABELS,
  userFullName,
  userRemovalBlock,
  validateUserForm,
  type DeskRole,
  type UserForm,
  type UserFormErrors,
  type UserRow,
} from 'src/utils/lead-desk';

const getRoleOptions = (isSuperAdmin: boolean): Array<{ value: string; label: string }> => [
  { value: '', label: '-- Choose Role --' },
  ...(isSuperAdmin ? [{ value: 'SUPER_ADMIN', label: 'Super Admin' }] : []),
  { value: 'ADMIN', label: 'Tenant Admin' },
  { value: 'COUNSELLOR', label: 'Counselor' },
  { value: 'TELECALLER', label: 'Telecaller' },
];

const roleColor = (role: DeskRole | string | null): string => (role === 'SUPER_ADMIN' ? 'crimson' : role === 'ADMIN' || role === 'TENANT_ADMIN' ? 'purple' : role === 'COUNSELLOR' ? 'blue' : role === 'TELECALLER' ? 'turquoise' : 'gray');

const formFromUser = (user: UserRow): UserForm => ({
  firstName: user.firstName,
  lastName: user.lastName,
  email: user.email,
  role: user.role ?? '',
  password: '',
  confirmPassword: '',
  isActive: user.isActive,
  reportsTo: user.reportsTo ?? '',
});

export const UsersScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const isMeSuperAdmin = ctx.identity?.role === 'SUPER_ADMIN' || ctx.role === 'SUPER_ADMIN';
  const users = useLiveData(fetchUsers, 30_000);
  // Super Admin has access to view EVERY account; Tenant Admin sees all tenant accounts (Super Admin accounts are hidden)
  const all = (users.data ?? []).filter((u) => isMeSuperAdmin || u.role !== 'SUPER_ADMIN');
  const [editing, setEditing] = useState<UserRow | 'new' | null>(null);
  const [form, setForm] = useState<UserForm>(EMPTY_USER_FORM);
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [removing, setRemoving] = useState<UserRow | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const creating = editing === 'new';

  const open = (target: UserRow | 'new'): void => {
    setForm(target === 'new' ? EMPTY_USER_FORM : formFromUser(target));
    setErrors({});
    setFailure(null);
    setDone(null);
    setEditing(target);
  };

  const submit = async (): Promise<void> => {
    const found = validateUserForm(form, creating);

    setErrors(found);

    if (Object.keys(found).length > 0 || editing === null) return;

    setBusy(true);
    setFailure(null);

    try {
      if (editing === 'new') {
        await createUser(form);
        setDone(`${userFullName(form)} was added.`);
      } else {
        await updateUser(editing.id, {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          role: form.role === '' ? undefined : form.role,
          isActive: form.isActive,
          reportsTo: form.role === 'TELECALLER' ? form.reportsTo : null,
          ...(form.password === '' ? {} : { password: form.password }),
        });
        setDone(`${userFullName(form)} was updated.`);
      }

      await Promise.all([users.refresh(), ctx.refresh()]);
      setEditing(null);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not save this user.');
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (removing === null) return;

    setBusy(true);
    setRemoveError(null);

    try {
      await deleteUser(removing.id);
      await users.refresh();
      setDone(`${userFullName(removing)} was removed.`);
      setRemoving(null);
    } catch (cause) {
      setRemoveError(cause instanceof Error ? cause.message : 'Could not remove this user.');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (user: UserRow): Promise<void> => {
    setFailure(null);

    try {
      await updateUser(user.id, { isActive: !user.isActive });
      await Promise.all([users.refresh(), ctx.refresh()]);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not change this user.');
    }
  };

  const q = query.trim().toLowerCase();
  const rows = all
    .filter((u) => q === '' || `${userFullName(u)} ${u.email}`.toLowerCase().includes(q))
    .sort((a, b) => userFullName(a).localeCompare(userFullName(b)));

  const columns: Column<UserRow>[] = [
    {
      key: 'name',
      header: 'Counselor Name',
      width: '1.6fr',
      render: (u) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: SHELL_SPACE[2], opacity: u.isActive ? 1 : 0.55 }}>
          <Avatar text={initials(userFullName(u))} />
          <span style={{ ...SHELL_TYPE.bodyMedium, color: shell.text }}>{userFullName(u)}</span>
          {!u.isActive && <ShellChip label="Inactive" color="gray" />}
        </span>
      ),
    },
    { key: 'email', header: 'Email Address', width: '2fr', render: (u) => u.email },
    { key: 'boss', header: 'Works under', width: '1.2fr', render: (u) => (u.role === 'TELECALLER' ? (all.find((x) => x.id === u.reportsTo) ? userFullName(all.find((x) => x.id === u.reportsTo) as UserRow) : '—') : u.role === 'COUNSELLOR' ? `${all.filter((x) => x.reportsTo === u.id && x.isActive).length} telecaller${all.filter((x) => x.reportsTo === u.id && x.isActive).length === 1 ? '' : 's'}` : '—') },
    { key: 'role', header: 'System Role', width: '1fr', render: (u) => <ShellChip label={u.role === null ? '—' : USER_ROLE_LABELS[u.role]} color={roleColor(u.role)} /> },
    { key: 'leads', header: 'Assigned Leads', width: '1fr', render: (u) => `${assignedCount(u, ctx.students)} Leads` },
    {
      key: 'actions',
      header: 'Actions',
      width: '120px',
      align: 'right',
      render: (u) => {
        if (!isMeSuperAdmin && u.role === 'SUPER_ADMIN') return <span>—</span>;
        return (
          <span style={{ display: 'inline-flex', gap: SHELL_SPACE[1] }}>
            <IconButton scheme={ctx.scheme} icon="edit" tone="green" label={`Edit ${userFullName(u)}`} onClick={() => open(u)} />
            <IconButton scheme={ctx.scheme} icon="ban" tone="red" label={u.isActive ? `Deactivate ${userFullName(u)}` : `Reactivate ${userFullName(u)}`} onClick={() => void toggleActive(u)} />
            <IconButton scheme={ctx.scheme} icon="trash" tone="red" label={`Delete ${userFullName(u)}`} onClick={() => { setRemoveError(null); setRemoving(u); }} />
          </span>
        );
      },
    },
  ];

  const removalBlock = removing === null ? null : userRemovalBlock(removing, all, ctx.students);

  return (
    <>
      <PageMasthead
        palette={getPalette(ctx.scheme)}
        scheme={ctx.scheme}
        title="Users Workspace"
        subtitle="Manage counselors, telecallers and admins: who can sign in and what they can reach."
        actions={
          ctx.profile.canManageUsers ? (
            <DeskButton scheme={ctx.scheme} primary onClick={() => open('new')}>
              + Add User
            </DeskButton>
          ) : undefined
        }
      />

      {done !== null && <Notice tone="green">{done}</Notice>}
      <ErrorLine scheme={ctx.scheme} message={users.error ?? failure} />

      <ShellCard scheme={ctx.scheme} padded={false}>
        <div style={{ padding: SHELL_SPACE[3] }}>
          <FilterBar scheme={ctx.scheme}>
            <DeskInput scheme={ctx.scheme} value={query} onChange={setQuery} placeholder="Search by name or email..." width="280px" />
            <LiveStamp scheme={ctx.scheme} at={users.updatedAt} />
          </FilterBar>
          <DataTable scheme={ctx.scheme} columns={columns} rows={rows} rowKey={(u) => u.id} empty={users.loading ? 'Loading…' : 'No users yet. Add the first one with + Add User.'} />
        </div>
      </ShellCard>

      {editing !== null && (
        <Drawer
          scheme={ctx.scheme}
          title={creating ? 'Add User Account' : `Edit ${userFullName(editing as UserRow)}`}
          subtitle={creating ? 'Create credentials and permissions for a new counselor or user' : (editing as UserRow).email}
          onClose={() => setEditing(null)}
          footer={
            <>
              <DeskButton scheme={ctx.scheme} primary disabled={busy} onClick={() => void submit()}>
                {busy ? 'Saving…' : creating ? 'Create Account' : 'Save'}
              </DeskButton>
              <DeskButton scheme={ctx.scheme} onClick={() => setEditing(null)}>
                Cancel
              </DeskButton>
              <ErrorLine scheme={ctx.scheme} message={failure} />
            </>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: SHELL_SPACE[3] }}>
            <Field scheme={ctx.scheme} label="First Name" required error={errors.firstName}>
              <DeskInput scheme={ctx.scheme} value={form.firstName} onChange={(firstName) => setForm({ ...form, firstName })} />
            </Field>
            <Field scheme={ctx.scheme} label="Last Name">
              <DeskInput scheme={ctx.scheme} value={form.lastName} onChange={(lastName) => setForm({ ...form, lastName })} />
            </Field>
          </div>
          <Field scheme={ctx.scheme} label="Email Address" required error={errors.email}>
            <DeskInput scheme={ctx.scheme} value={form.email} onChange={(email) => setForm({ ...form, email })} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: SHELL_SPACE[3] }}>
            <Field scheme={ctx.scheme} label={creating ? 'Password' : 'New Password'} required={creating} error={errors.password}>
              <DeskInput scheme={ctx.scheme} value={form.password} onChange={(password) => setForm({ ...form, password })} placeholder={creating ? '' : 'Leave blank to keep the current one'} />
            </Field>
            <Field scheme={ctx.scheme} label="Confirm Password" required={creating} error={errors.confirmPassword}>
              <DeskInput scheme={ctx.scheme} value={form.confirmPassword} onChange={(confirmPassword) => setForm({ ...form, confirmPassword })} />
            </Field>
          </div>
          <Field scheme={ctx.scheme} label="Select Role" required error={errors.role}>
            <DeskSelect scheme={ctx.scheme} value={form.role} onChange={(role) => setForm({ ...form, role: role === '' ? '' : (role as DeskRole), reportsTo: role === 'TELECALLER' ? form.reportsTo : '' })} options={getRoleOptions(isMeSuperAdmin)} />
          </Field>
          {form.role === 'TELECALLER' && (
            <Field scheme={ctx.scheme} label="Works under (counsellor)" required error={errors.reportsTo}>
              <DeskSelect
                scheme={ctx.scheme}
                value={form.reportsTo}
                onChange={(reportsTo) => setForm({ ...form, reportsTo })}
                options={[{ value: '', label: '-- Choose Counsellor --' }, ...activeCounsellors(all).map((c) => ({ value: c.id, label: userFullName(c) }))]}
              />
            </Field>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[2], ...SHELL_TYPE.body, color: shell.textSecondary }}>
            <input type="checkbox" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
            Account is active
          </label>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginTop: SHELL_SPACE[3] }}>
            Renaming a counselor or telecaller keeps their students: they follow the person, not the old spelling.
          </div>
        </Drawer>
      )}

      {removing !== null && (
        <ConfirmDialog
          scheme={ctx.scheme}
          title="Delete this user?"
          message={removalBlock ?? <>This removes <strong>{userFullName(removing)}</strong>'s account. It cannot be undone.</>}
          confirmLabel={removalBlock === null ? 'Delete user' : 'OK'}
          busy={busy}
          error={removeError}
          onConfirm={() => (removalBlock === null ? void confirmRemove() : setRemoving(null))}
          onCancel={() => setRemoving(null)}
        />
      )}
    </>
  );
};
